import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Calendar, DollarSign, User, Phone, Mail, MapPin, Baby } from "lucide-react";
import { API, DAYS, TIMES, calculateLessonDates, formatDate, isFriday, isSaturday, getPrice, getWeeks, getTimesForDayType, ALL_TIMES } from "@/lib/utils";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_pool-lesson-book/artifacts/c9s2nl0s_rr%20logo.png";
const HERO_URL = "https://images.unsplash.com/photo-1592484806287-7bc9c8af5405?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHwyfHxraWRzJTIwc3dpbW1pbmclMjBwb29sJTIwc3VtbWVyfGVufDB8fHx8MTc3NDYzMTk2MHww&ixlib=rb-4.1.0&q=85&w=1200";

export default function RegistrationPage() {
  const navigate = useNavigate();
  const [lifeguards, setLifeguards] = useState([]);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    parent_first_name: "",
    parent_last_name: "",
    email: "",
    phone: "",
    address: "",
    city: "",
    state: "",
    zip_code: "",
    child_first_name: "",
    child_last_name: "",
    additional_child_info: "",
    swim_level: "",
    lifeguard_preference: "",
    choice1_day: "",
    choice1_time: "",
  });

  const [lessonDates, setLessonDates] = useState([]);
  const [price, setPrice] = useState(0);
  const [lifeguardAvailability, setLifeguardAvailability] = useState([]);
  const [bookedSlots, setBookedSlots] = useState([]);
  const [childDivision, setChildDivision] = useState(null);
  const [showNavigatorDisclaimer, setShowNavigatorDisclaimer] = useState(false);
  const [duplicateError, setDuplicateError] = useState(null);

  const isNavigatorOrCIT = childDivision === 'Navigator' || childDivision === 'CIT';
  const navigatorAllowedDays = ['monday', 'friday', 'saturday'];

  useEffect(() => {
    fetchLifeguards();
  }, []);

  useEffect(() => {
    if (formData.choice1_day) {
      const fri = isFriday(formData.choice1_day);
      const dates = calculateLessonDates(formData.choice1_day, fri);
      setLessonDates(dates);
      setPrice(getPrice(formData.choice1_day));
    } else {
      setLessonDates([]);
      setPrice(0);
    }
  }, [formData.choice1_day]);

  useEffect(() => {
    if (formData.lifeguard_preference) {
      fetchLifeguardAvailability(formData.lifeguard_preference);
      setFormData(prev => ({ ...prev, choice1_day: "", choice1_time: "" }));
    } else {
      setLifeguardAvailability([]);
      setBookedSlots([]);
    }
  }, [formData.lifeguard_preference]);

  // Check enrollment and division when child name changes
  useEffect(() => {
    const first = formData.child_first_name.trim();
    const last = formData.child_last_name.trim();
    if (first.length >= 2 && last.length >= 2) {
      const timeout = setTimeout(async () => {
        try {
          const res = await axios.get(`${API}/check-enrollment`, {
            params: { first_name: first, last_name: last }
          });
          if (res.data.enrolled) {
            setChildDivision(res.data.division);
            if (res.data.division === 'Navigator' || res.data.division === 'CIT') {
              setShowNavigatorDisclaimer(true);
              if (formData.choice1_day && formData.choice1_day !== 'monday' && formData.choice1_day !== 'friday') {
                setFormData(prev => ({ ...prev, choice1_day: "", choice1_time: "" }));
              }
            } else {
              setShowNavigatorDisclaimer(false);
            }
          } else {
            setChildDivision(null);
            setShowNavigatorDisclaimer(false);
          }
        } catch {
          setChildDivision(null);
          setShowNavigatorDisclaimer(false);
        }
      }, 500);
      return () => clearTimeout(timeout);
    } else {
      setChildDivision(null);
      setShowNavigatorDisclaimer(false);
    }
  }, [formData.child_first_name, formData.child_last_name]);

  const fetchLifeguards = async () => {
    try {
      const res = await axios.get(`${API}/lifeguards`);
      setLifeguards(res.data.filter(lg => lg.active));
    } catch (err) {
      console.error("Failed to fetch lifeguards:", err);
    }
  };

  const fetchLifeguardAvailability = async (name) => {
    try {
      const res = await axios.get(`${API}/lifeguard-availability/${encodeURIComponent(name)}`);
      setLifeguardAvailability(res.data.availability || []);
      setBookedSlots(res.data.booked_slots || []);
    } catch (err) {
      console.error("Failed to fetch availability:", err);
      setLifeguardAvailability([]);
      setBookedSlots([]);
    }
  };

  const isSlotBooked = (day, time) => {
    return bookedSlots.some(s => s.day === day.toLowerCase() && s.time === time);
  };

  // Get days where lifeguard has at least one valid time slot for that day type
  const getAvailableDays = () => {
    if (!formData.lifeguard_preference || lifeguardAvailability.length === 0) return [];
    const days = new Set();
    lifeguardAvailability.forEach(slot => {
      const day = slot.day.toLowerCase();
      const validTimes = getTimesForDayType(day);
      if (validTimes.includes(slot.time)) {
        days.add(day);
      }
    });
    return DAYS.filter(d => days.has(d));
  };

  // Get times for selected day from lifeguard's availability, marking booked ones
  const getTimesForDay = () => {
    if (!formData.choice1_day || lifeguardAvailability.length === 0) return [];
    const validTimes = getTimesForDayType(formData.choice1_day);
    const timeOrder = ALL_TIMES;
    return lifeguardAvailability
      .filter(slot => slot.day.toLowerCase() === formData.choice1_day.toLowerCase() && validTimes.includes(slot.time))
      .map(slot => ({
        time: slot.time,
        booked: isSlotBooked(formData.choice1_day, slot.time)
      }))
      .sort((a, b) => timeOrder.indexOf(a.time) - timeOrder.indexOf(b.time));
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.choice1_day || !formData.choice1_time) {
      toast.error("Please select a Day and Time");
      return;
    }

    if (!formData.lifeguard_preference) {
      toast.error("Please select a Lifeguard");
      return;
    }

    if (isSlotBooked(formData.choice1_day, formData.choice1_time)) {
      toast.error("That time slot is already booked. Please select a different one.");
      return;
    }

    setLoading(true);
    setDuplicateError(null);
    try {
      const res = await axios.post(`${API}/register`, formData);
      toast.success("Registration submitted! Please sign the contract.");
      navigate(`/contract/${res.data.id}`);
    } catch (err) {
      if (err.response?.status === 409) {
        setDuplicateError(err.response.data.detail);
      } else {
        toast.error(err.response?.data?.detail || "Registration failed. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const availableDays = getAvailableDays();
  const timesForDay = getTimesForDay();

  return (
    <div className="min-h-screen bg-[#FDFBF7] relative">
      <div className="pattern-bg" />
      
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-slate-100">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <a href="/" className="text-sm text-slate-500 hover:text-[#109FE9] transition-colors font-semibold" data-testid="back-home-link">
              ← Home
            </a>
            <img src={LOGO_URL} alt="Rolling River Day Camp" className="h-16 md:h-20" />
          </div>
          <a 
            href="/admin" 
            className="text-sm text-slate-500 hover:text-[#109FE9] transition-colors"
            data-testid="admin-link"
          >
            Admin Login
          </a>
        </div>
      </header>

      {/* Hero Section */}
      <div className="relative h-48 md:h-64 overflow-hidden">
        <img 
          src={HERO_URL} 
          alt="Kids swimming" 
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
        <div className="absolute bottom-6 left-0 right-0 text-center">
          <h1 className="font-heading text-3xl md:text-5xl font-bold text-white drop-shadow-lg">
            July-August Lessons
          </h1>
          <p className="font-body text-white/90 mt-2 text-lg">Rolling River Campers Only</p>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-3xl mx-auto px-4 py-8 md:py-12">
        {/* Disclaimer */}
        <div className="disclaimer-box" data-testid="disclaimer-box">
          <p className="disclaimer-text">
            Swim lessons are available exclusively for 2026 enrolled campers during the camp season (June 29 – August 20). Lessons are offered Monday-Saturday. Registration is completed online, and payment is due in cash to the camp office within 7 days of registration or your selection will be cancelled.
          </p>
        </div>

        {/* Registration Form */}
        <form onSubmit={handleSubmit} className="card space-y-8 animate-fade-in">
          {/* Parent Information */}
          <section>
            <h2 className="font-heading text-2xl font-bold text-[#109FE9] mb-6 flex items-center gap-2">
              <User className="w-6 h-6" /> Parent Information
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-sm font-bold text-slate-700 uppercase tracking-wider mb-2 block">
                  First Name *
                </Label>
                <Input
                  data-testid="parent-first-name"
                  className="form-input w-full"
                  value={formData.parent_first_name}
                  onChange={(e) => handleChange("parent_first_name", e.target.value)}
                  required
                />
              </div>
              <div>
                <Label className="text-sm font-bold text-slate-700 uppercase tracking-wider mb-2 block">
                  Last Name *
                </Label>
                <Input
                  data-testid="parent-last-name"
                  className="form-input w-full"
                  value={formData.parent_last_name}
                  onChange={(e) => handleChange("parent_last_name", e.target.value)}
                  required
                />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              <div>
                <Label className="text-sm font-bold text-slate-700 uppercase tracking-wider mb-2 block">
                  <Mail className="w-4 h-4 inline mr-1" /> Email *
                </Label>
                <Input
                  data-testid="parent-email"
                  type="email"
                  className="form-input w-full"
                  value={formData.email}
                  onChange={(e) => handleChange("email", e.target.value)}
                  required
                />
              </div>
              <div>
                <Label className="text-sm font-bold text-slate-700 uppercase tracking-wider mb-2 block">
                  <Phone className="w-4 h-4 inline mr-1" /> Phone *
                </Label>
                <Input
                  data-testid="parent-phone"
                  type="tel"
                  className="form-input w-full"
                  value={formData.phone}
                  onChange={(e) => handleChange("phone", e.target.value)}
                  required
                />
              </div>
            </div>
            <div className="mt-4">
              <Label className="text-sm font-bold text-slate-700 uppercase tracking-wider mb-2 block">
                <MapPin className="w-4 h-4 inline mr-1" /> Street Address *
              </Label>
              <Input
                data-testid="parent-address"
                className="form-input w-full"
                placeholder="123 Main Street"
                value={formData.address}
                onChange={(e) => handleChange("address", e.target.value)}
                required
              />
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
              <div className="col-span-2 md:col-span-1">
                <Label className="text-sm font-bold text-slate-700 uppercase tracking-wider mb-2 block">
                  City *
                </Label>
                <Input
                  data-testid="parent-city"
                  className="form-input w-full"
                  placeholder="City"
                  value={formData.city}
                  onChange={(e) => handleChange("city", e.target.value)}
                  required
                />
              </div>
              <div>
                <Label className="text-sm font-bold text-slate-700 uppercase tracking-wider mb-2 block">
                  State *
                </Label>
                <Input
                  data-testid="parent-state"
                  className="form-input w-full"
                  placeholder="NY"
                  maxLength={2}
                  value={formData.state}
                  onChange={(e) => handleChange("state", e.target.value.toUpperCase())}
                  required
                />
              </div>
              <div>
                <Label className="text-sm font-bold text-slate-700 uppercase tracking-wider mb-2 block">
                  Zip Code *
                </Label>
                <Input
                  data-testid="parent-zip"
                  className="form-input w-full"
                  placeholder="12345"
                  maxLength={10}
                  value={formData.zip_code}
                  onChange={(e) => handleChange("zip_code", e.target.value)}
                  required
                />
              </div>
            </div>
          </section>

          {/* Child Information */}
          <section>
            <h2 className="font-heading text-2xl font-bold text-[#109FE9] mb-6 flex items-center gap-2">
              <Baby className="w-6 h-6" /> Child Information
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-sm font-bold text-slate-700 uppercase tracking-wider mb-2 block">
                  First Name *
                </Label>
                <Input
                  data-testid="child-first-name"
                  className="form-input w-full"
                  value={formData.child_first_name}
                  onChange={(e) => handleChange("child_first_name", e.target.value)}
                  required
                />
              </div>
              <div>
                <Label className="text-sm font-bold text-slate-700 uppercase tracking-wider mb-2 block">
                  Last Name *
                </Label>
                <Input
                  data-testid="child-last-name"
                  className="form-input w-full"
                  value={formData.child_last_name}
                  onChange={(e) => handleChange("child_last_name", e.target.value)}
                  required
                />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              <div>
                <Label className="text-sm font-bold text-slate-700 uppercase tracking-wider mb-2 block">
                  Swim Level *
                </Label>
                <Select
                  value={formData.swim_level}
                  onValueChange={(value) => handleChange("swim_level", value)}
                  required
                >
                  <SelectTrigger data-testid="swim-level-select" className="form-input">
                    <SelectValue placeholder="Select level" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Unknown">Unknown</SelectItem>
                    <SelectItem value="PSA1">PSA1</SelectItem>
                    <SelectItem value="PSA2">PSA2</SelectItem>
                    <SelectItem value="1">Level 1</SelectItem>
                    <SelectItem value="2">Level 2</SelectItem>
                    <SelectItem value="3">Level 3</SelectItem>
                    <SelectItem value="4">Level 4</SelectItem>
                    <SelectItem value="5">Level 5</SelectItem>
                    <SelectItem value="6">Level 6</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-sm font-bold text-slate-700 uppercase tracking-wider mb-2 block">
                  Lifeguard *
                </Label>
                <Select
                  value={formData.lifeguard_preference}
                  onValueChange={(value) => handleChange("lifeguard_preference", value)}
                  required
                >
                  <SelectTrigger data-testid="lifeguard-select" className="form-input">
                    <SelectValue placeholder="Select lifeguard" />
                  </SelectTrigger>
                  <SelectContent>
                    {lifeguards.map((lg) => (
                      <SelectItem key={lg.id} value={lg.name}>
                        {lg.name}{lg.age ? ` (${lg.age}, ${lg.gender || ''})` : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="mt-4">
              <Label className="text-sm font-bold text-slate-700 uppercase tracking-wider mb-2 block">
                Additional Child Information
              </Label>
              <Textarea
                data-testid="additional-child-info"
                className="form-input w-full min-h-[80px]"
                placeholder="Any additional information about your child (allergies, special needs, notes for the instructor, etc.)"
                value={formData.additional_child_info}
                onChange={(e) => handleChange("additional_child_info", e.target.value)}
              />
            </div>
            <p className="text-sm text-slate-500 mt-4 italic">
              If you are unsure about which instructor to select please reach out to our aquatics supervisor at{" "}
              <a href="mailto:aquatics@rollingriver.com" className="text-[#109FE9] underline">aquatics@rollingriver.com</a>.
            </p>
          </section>

          {/* Lesson Schedule */}
          <section>
            <h2 className="font-heading text-2xl font-bold text-[#109FE9] mb-6 flex items-center gap-2">
              <Calendar className="w-6 h-6" /> Lesson Schedule
            </h2>
            
            <div className="bg-[#E6F5FD] rounded-2xl p-6">
              {showNavigatorDisclaimer && (
                <div className="bg-[#FEF3C7] border border-[#F59E0B] rounded-xl p-4 mb-4" data-testid="navigator-disclaimer">
                  <p className="text-sm text-[#92400E] font-bold text-center">
                    Due to the navigator and CIT trip schedule, swim lessons are only offered to Navigators and CITs on Mondays, Fridays, and Saturdays.
                  </p>
                </div>
              )}
              {!formData.lifeguard_preference && (
                <p className="text-sm text-slate-500 text-center py-4">Please select a lifeguard first to see available times.</p>
              )}
              {formData.lifeguard_preference && lifeguardAvailability.length === 0 && (
                <p className="text-sm text-slate-500 text-center py-4">No availability set for this lifeguard yet. Please contact the camp office.</p>
              )}
              {formData.lifeguard_preference && lifeguardAvailability.length > 0 && (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-sm font-bold text-slate-700 uppercase tracking-wider mb-2 block">
                        Day *
                      </Label>
                      <Select
                        value={formData.choice1_day}
                        onValueChange={(value) => {
                          handleChange("choice1_day", value);
                          handleChange("choice1_time", "");
                        }}
                        required
                      >
                        <SelectTrigger data-testid="day-select" className="form-input bg-white">
                          <SelectValue placeholder="Select day" />
                        </SelectTrigger>
                        <SelectContent>
                          {availableDays.map((day) => {
                            const restrictedDay = isNavigatorOrCIT && !navigatorAllowedDays.includes(day);
                            return (
                              <SelectItem key={day} value={day} disabled={restrictedDay}>
                                {day.charAt(0).toUpperCase() + day.slice(1)}
                                {restrictedDay ? " (Not available)" : ""}
                              </SelectItem>
                            );
                          })}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-sm font-bold text-slate-700 uppercase tracking-wider mb-2 block">
                        Time *
                      </Label>
                      <Select
                        value={formData.choice1_time}
                        onValueChange={(value) => handleChange("choice1_time", value)}
                        required
                        disabled={!formData.choice1_day}
                      >
                        <SelectTrigger data-testid="time-select" className="form-input bg-white">
                          <SelectValue placeholder={formData.choice1_day ? "Select time" : "Select day first"} />
                        </SelectTrigger>
                        <SelectContent>
                          {timesForDay.map(({ time, booked }) => (
                            <SelectItem key={time} value={time} disabled={booked}>
                              {time}{booked ? " (Booked)" : ""}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  {lessonDates.length > 0 && (
                    <div className="mt-4 p-4 bg-white rounded-xl">
                      <p className="text-sm font-semibold text-slate-700 mb-2">
                        {getWeeks(formData.choice1_day)} — ${price}
                      </p>
                      <p className="text-xs text-slate-500">
                        <strong>Dates:</strong> {lessonDates.map(d => formatDate(d)).join(", ")}
                      </p>
                    </div>
                  )}
                </>
              )}
            </div>
          </section>

          {/* Cancellation Disclaimer */}
          <div className="bg-[#E6F5FD] rounded-xl p-4" data-testid="cancellation-disclaimer">
            <p className="text-sm text-black font-bold text-center" style={{ fontSize: '14px' }}>
              Please carefully review your selected date and time to ensure it works for both you and your child. Cancellations initiated by the family cannot be guaranteed a reschedule. In the event that camp or the instructor must cancel a session, we will make every effort to find a suitable alternative time.
            </p>
          </div>

          {/* Payment Warning */}
          <div className="bg-red-50 border-2 border-red-300 rounded-xl p-5 text-center" data-testid="payment-warning">
            <p className="text-lg font-bold text-red-700">
              Payment is due within 7 days or your lesson will be cancelled.
            </p>
          </div>

          {/* Pricing */}
          {price > 0 && (
            <section>
              <div className="bg-gradient-to-r from-[#109FE9] to-[#0E8ACB] rounded-2xl p-6 text-white">
                <div className="flex items-center gap-3">
                  <DollarSign className="w-8 h-8" />
                  <div>
                    <p className="text-white/80 text-sm">{formData.choice1_day?.charAt(0).toUpperCase() + formData.choice1_day?.slice(1)} at {formData.choice1_time}</p>
                    <p className="font-heading text-3xl font-bold" data-testid="lesson-price">
                      ${price.toFixed(2)}
                    </p>
                    <p className="text-white/70 text-xs">{getWeeks(formData.choice1_day)} | Lifeguard: {formData.lifeguard_preference || "Not selected"}</p>
                  </div>
                </div>
              </div>
            </section>
          )}

          {/* Duplicate Error */}
          {duplicateError && (
            <div className="bg-[#FEF2F2] border border-red-300 rounded-xl p-4" data-testid="duplicate-error">
              <p className="text-sm text-red-700 font-semibold text-center">{duplicateError}</p>
            </div>
          )}

          {/* Submit Button */}
          <Button
            type="submit"
            disabled={loading}
            className="btn-primary w-full"
            data-testid="submit-registration-btn"
          >
            {loading ? "Submitting..." : "Continue to Contract Signing"}
          </Button>
        </form>
      </main>

      {/* Footer */}
      <footer className="bg-white border-t border-slate-100 py-6 mt-12">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <p className="text-sm text-slate-500">
            &copy; 2026 Rolling River Day Camp. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}
